import React, { useEffect, useState } from 'react';
import { Box, PagePanel } from 'edenred-ui';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { SELECTED_BENEFIT_PARAM } from '@epi-constants/benefitTypes';
import { useBenefitAmounts } from '@epi-containers/ManageBenefitGroups/hooks';
import { IAvailableBenefits } from '@epi-models/containers/ManageBenefits';
import { OnboardingSupportInformation } from '@epi-components/OnboardingSupportInformation';
import { StyledLinkWithHref } from '@epi-shared/components/StyledLink/StyledLink.styles';
import { showNotification } from '@epi-actions/overlays';
import OnboardingStepper from '@epi-components/OnboardingStepper/OnboardingStepper';
import { useOnboardingWizard } from '@epi-shared/hooks';
import { goToPage } from '@epi-actions/navigation';
import { routerPaths } from '@epi-constants/routerPaths';
import { ActivateBenefitGroupsForm } from '@epi-forms/ActivateBenefitGroups/ActivateBenefitGroups';
import { useCreateGroupSettingsMutation } from '@epi-api/manageBenefits/manageBenefitsApi';

import { BenefitSettingsModal } from './components/BenefitSettingsModal/BenefitSettingsModal';
import { BenefitTypeTile } from './components';
import { useManageBenefits } from './hooks';
import {
  InactiveBenefitsTitle,
  MyChoiceBottomInformation
} from './ManageBenefits.styles';

export const ManageBenefits = () => {
  const { t } = useTranslation();
  const {
    activeAvailableBenefits,
    inactiveAvailableBenefits,
    refetchAvailableBenefits
  } = useManageBenefits();
  const { isOnBoardingActive } = useOnboardingWizard();
  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const [createGroupSettings, { isSuccess, isError, reset }] =
    useCreateGroupSettingsMutation();

  const hidePageTitle =
    (activeAvailableBenefits?.length && !inactiveAvailableBenefits?.length) ||
    (!activeAvailableBenefits?.length && inactiveAvailableBenefits?.length);

  const [showInformationModal, setShowInformationModal] = useState(false);

  const [selectedBenefits, setSelectedBenefits] = useState<
    IAvailableBenefits[]
  >([]);
  const dispatch = useDispatch();

  const shouldGoToBeneficiariesInOnboarding =
    isOnBoardingActive &&
    (inactiveAvailableBenefits?.length === 0 ||
      (activeAvailableBenefits?.length && !selectedBenefits.length));

  const handleCloseModal = (isSuccess?: boolean) => {
    setShowInformationModal(false);
    if (isSuccess === undefined) return;

    if (isSuccess) {
      refetchAvailableBenefits();
    }
    const type = isSuccess ? 'success' : 'error';
    const message = isSuccess
      ? 'components.manage_benefit_lunch.group_added_notification'
      : 'messages.error';

    dispatch(showNotification({ message }, type));
    setSelectedBenefits([]);
  };

  const toggleBenefit = (benefitType: string) => {
    setSelectedBenefits(prev => {
      if (prev.some(s => s.type === benefitType)) {
        return prev.filter(b => b.type !== benefitType);
      }

      const existingBenefit = inactiveAvailableBenefits.find(
        b => b.type.toLowerCase() === benefitType.toLowerCase()
      );

      if (!existingBenefit) return prev;

      return [...prev, existingBenefit];
    });
  };

  const goToOnboardingNextPage = () => {
    const selectedBenefitsIds = selectedBenefits
      .map(benefit => benefit.id)
      .join(',');

    const nextPagePath = shouldGoToBeneficiariesInOnboarding
      ? routerPaths.beneficiaries
      : `${routerPaths.activateBenefits}?${SELECTED_BENEFIT_PARAM}=${selectedBenefitsIds}`;

    dispatch(goToPage(nextPagePath));
  };

  const handleGoBack = () => {
    dispatch(goToPage(routerPaths.home));
  };

  const benefitMaxAmounts = useBenefitAmounts();
  const isActivateEnabled = selectedBenefits.length > 0;

  useEffect(() => {
    const benefitType = urlParams.get(SELECTED_BENEFIT_PARAM);
    if (!benefitType) return;

    setSelectedBenefits(prev => {
      const alreadySelected = prev.some(
        b => b.type?.toLowerCase() === benefitType.toLowerCase()
      );
      if (alreadySelected) return prev;

      const benefitToAdd = inactiveAvailableBenefits.find(
        s => s.type?.toLowerCase() === benefitType.toLowerCase()
      );

      if (!benefitToAdd) return prev;

      return [...prev, benefitToAdd];
    });
  }, [urlParams, inactiveAvailableBenefits]);

  return (
    <Box>
      <OnboardingStepper />
      <PagePanel
        title={
          hidePageTitle
            ? t('components.manage_benefits.new.title')
            : t('components.manage_benefits.active_benefits')
        }
        footer={
          inactiveAvailableBenefits?.length || isOnBoardingActive
            ? {
                submitText: shouldGoToBeneficiariesInOnboarding
                  ? t('components.onboarding.buttons.next')
                  : t('components.manage_benefits.activate_benefits'),
                submitButtonWidth: '240px',
                disableSubmit: shouldGoToBeneficiariesInOnboarding
                  ? false
                  : !isActivateEnabled,
                onSubmit: () =>
                  isOnBoardingActive
                    ? goToOnboardingNextPage()
                    : setShowInformationModal(true),
                backText: isOnBoardingActive
                  ? t('components.onboarding.buttons.back')
                  : undefined,
                onBack: isOnBoardingActive ? handleGoBack : undefined
              }
            : undefined
        }
      >
        <Box>
          <Box display="flex" flexWrap="wrap">
            {activeAvailableBenefits.length > 0 &&
              activeAvailableBenefits.map(benefit => (
                <BenefitTypeTile
                  key={benefit.id}
                  benefit={benefit.type?.toLowerCase()}
                  active
                />
              ))}
          </Box>

          {inactiveAvailableBenefits.length > 0 && (
            <>
              {!hidePageTitle && (
                <InactiveBenefitsTitle>
                  {t('components.manage_benefits.inactive_benefits')}
                </InactiveBenefitsTitle>
              )}
              <Box display="flex" flexWrap="wrap">
                {inactiveAvailableBenefits.map(benefit => (
                  <BenefitTypeTile
                    key={benefit.id}
                    benefit={benefit.type?.toLowerCase()}
                    active={false}
                    checked={selectedBenefits.some(
                      x => x.type?.toLowerCase() === benefit.type?.toLowerCase()
                    )}
                    onToggle={() => toggleBenefit(benefit.type)}
                  />
                ))}
              </Box>
            </>
          )}
        </Box>

        <ActivateBenefitGroupsForm
          selectedBenefits={selectedBenefits}
          mutationTrigger={createGroupSettings}
        >
          <BenefitSettingsModal
            showInformationModal={showInformationModal}
            closeModal={handleCloseModal}
            selectedBenefits={selectedBenefits}
            benefitMaxAmounts={benefitMaxAmounts}
            isError={isError}
            isSuccess={isSuccess}
            resetMutation={reset}
          />
        </ActivateBenefitGroupsForm>
        <MyChoiceBottomInformation>
          <Trans
            i18nKey="components.manage_benefits.footer_info"
            components={{
              a: (
                <StyledLinkWithHref
                  target="_blank"
                  rel="noopener noreferrer"
                  href={t(`components.manage_benefits.my_choice_link_url`)}
                />
              )
            }}
          />
        </MyChoiceBottomInformation>
      </PagePanel>
      <OnboardingSupportInformation />
    </Box>
  );
};
